import { useEffect } from "react";
import { useSelector } from "react-redux";

import { Modal } from "@/components/modal/modal";
import { Button, FormSize } from "ui/ecosystems/forms";
import { YoutubeChannel } from "api/social-account-connect/youtube";

import { YoutubeConnectInstructions } from "../youtube-connect-instructions";
import { YoutubeChannelList } from "../youtube-channel-list";
import { NeedHelpButton } from "../need-help-button";
import {
  addYtChannelFx,
  fetchYoutubeChannelsFx,
  loginWithGoogleFx,
  navigateToAccountsFx,
} from "./model";
import { getConnectButtonStateFactory } from "../connect-button";
import { PlatformTypes } from "features/post";
import {
  dispatchAppAction,
  selectSocialConnectYoutubeState,
  socialAccountConnectActions,
} from "../../redux-model";

interface YoutubeConnectDialogProps {
  show: boolean;

  onHide(): void;

  onBack(): void;

  isAlreadyAdded(chan: YoutubeChannel): boolean;
}

export function YoutubeConnectDialog({
  show,
  onHide,
  onBack,
  isAlreadyAdded,
}: YoutubeConnectDialogProps) {
  const { channels, isLoggingIn, connectingChannelId, isGettingChannels } =
    useSelector(selectSocialConnectYoutubeState);

  useEffect(() => {
    if (!show) {
      return;
    }

    fetchYoutubeChannelsFx();

    return () => {
      dispatchAppAction(socialAccountConnectActions.resetYoutubeDialogState());
    };
  }, [show]);

  // in case if we will have only one account and it's account already connected,
  // no need to open add YT account modal again.
  if (channels?.length === 1 && isAlreadyAdded(channels[0])) {
    navigateToAccountsFx();
  }

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton onHide={onHide}>
        <Modal.Title>YouTube connection</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {channels && channels.length > 0 ? (
          <YoutubeChannelList
            channels={channels}
            onConnect={(channel) => addYtChannelFx(channel)}
            getConnectButtonState={getConnectButtonStateFactory({
              isConnected: isAlreadyAdded,
              isConnecting: (profile) => profile.id === connectingChannelId,
              isDisabled: () => Boolean(connectingChannelId) || isLoggingIn,
            })}
            LoginWithGoogleButtonProps={{
              disabled: isLoggingIn || isGettingChannels,
              onClick: loginWithGoogleFx,
            }}
          />
        ) : (
          <YoutubeConnectInstructions
            ButtonProps={{
              disabled: isLoggingIn || isGettingChannels,
              onClick: () => void loginWithGoogleFx(),
            }}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button text size={FormSize.large} onClick={onBack}>
          Back
        </Button>
        <NeedHelpButton platform={PlatformTypes.youtube} chatQuery="accounts" />
      </Modal.Footer>
    </Modal>
  );
}
